import React, {
  memo, useCallback, useEffect, useState, useContext, useMemo,
} from 'react';
import { useQuery } from 'react-query';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useParams } from 'react-router';
import moment from 'moment';

import DetailHeader from '../components/DetailHeader';
import ParametersList from './ParametersList';
import {
  getDatasetSummaries,
  getPlatformMetadata,
  parameterConfigurations,
  getObsLatestData,
  getRegisteredParameters,
  getObsDataV2,
} from '../../../services/dataset.service';
import { getOrganizations } from '../../../services/organization.service';
import MapQueryTypes from '../../../services/query-types/map';
import {
  formatParameterObservation,
  getParameters,
  getLatestParameterDate,
  normalizeParameterObs,
} from '../../../utils';
import './styles.scss';
import MarkerStatus from '../../../components/icons/MarkerStatus';
import UserContext from '../../../contexts/UserContext';
import { brandingConfig } from '../../../config';
import CollectionStatusAlert from './CollectionStatusAlert';

const DataConsoleDetail = () => {
  const location = useLocation();

  const { id, platformId, obsDatasetId } = useParams();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const dateQueryParameter = new URLSearchParams(location.search).get('date');

  const [headerInfo, setHeaderInfo] = useState();
  const [organization, setOrganization] = useState();
  const [dataStartDate, setDataStartDate] = useState(dateQueryParameter || moment().format('YYYY-MM-DD'));
  const [obsDataset, setObsDataset] = useState();
  const [datasetIds, setDatasetIds] = useState(id ? [Number(id)] : []);

  const { unitPreferences } = useContext(UserContext);

  const isPlatformsDataConsole = !!platformId && !!obsDatasetId && window.location.pathname.includes('/platforms/');
  const shouldRenderStatusMarker = !isPlatformsDataConsole;

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const collectionStatus = searchParams.get('collectionStatus') || null;

  // Would like to use the DataContext hooks for these queries,
  // but they were causing infinite rerenders - opted not to sink more time into it right now
  const {
    isLoading: obsDatasetSummariesIsLoading,
    data: obsDatasetSummariesResult,
  } = useQuery(
    [MapQueryTypes.REST_OBS_DATASET_SUMMARIES, platformId],
    () => getDatasetSummaries({
      platformId,
    }),
    {
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: obsLatestDataResult,
    isLoading: obsLatestIsLoading,
  } = useQuery(
    MapQueryTypes.REST_OBSERVATIONAL_LATEST_DATA,
    () => getObsLatestData(collectionStatus),
    { refetchOnWindowFocus: false },
  );

  const {
    data: parameterConfigurationsResult,
    isLoading: parameterConfigurationsIsLoading,
  } = useQuery(MapQueryTypes.REST_PARAMETER_CONFIGURATIONS, parameterConfigurations, { refetchOnWindowFocus: false });

  const {
    data: registeredParametersResult,
    isLoading: registeredParametersIsLoading,
  } = useQuery(MapQueryTypes.REST_PARAMETERS_REGISTERED_CONSOLEDETAIL, getRegisteredParameters, { refetchOnWindowFocus: false });

  /**
   * Get parameter IDs by obs dataset
   * @param {object} obsDataset
   * @returns {Array} parameterIds
   */
  const getObsDatasetParameterIds = (obsDataset) => {
    const platformId = obsDataset?.obs_dataset_platform_assignment?.platform_id;
    if (!platformId || registeredParametersResult?.length < 1) {
      return [];
    }
    return registeredParametersResult
      ?.filter((platform) => platform.platform_id === platformId)
      .map((parameter) => parameter.parameter_id);
  };

  const parameterIds = useMemo(() => getObsDatasetParameterIds(obsDataset), [
    parameterConfigurationsResult?.length,
    obsDataset?.obs_dataset_id,
    registeredParametersResult?.length,
  ]);

  const filterValue = {
    startDate: dataStartDate,
    obsDatasetId: datasetIds,
    parameterIds,
    collectionStatus,
  };

  const {
    data: obsDataV2,
    isLoading: obsDataV2IsLoading,
  } = useQuery(
    [MapQueryTypes.REST_OBSERVATIONAL_V2_DATA, filterValue],
    () => getObsDataV2(filterValue), {
      refetchOnWindowFocus: false,
      enabled: !!dataStartDate && !!datasetIds.length && !!registeredParametersResult,
    },
  );

  /**
   * Transforms observations from /v2/obs endpoint into the structure of /v1/obs which was previously built on
   * TypeScript would be nice here, see example object shapes below
   *
   * @param {Array} obs
    {
      "timestamps": [1629308832, 1629308842, 1629308852],
      "parameter_obs": {
        "values": {
          "476": [1, 2, 3],
          "478": [10, 20, null],
          "parameter_id_n": [100, 200, 300]
        },
      }
    }
   * @returns {Array}
      "parameters": [
      {
        "parameter_id": 42,
        "observations": [
          {
            "timestamp": "1970-01-01T00:00:00.000Z",
            "value": 1.23456,
            "latitude": 41.69947,
            "longitude": -83.258675
            },
          ]
        }
      ]
   */
  const transformObsDataV2 = ((obs) => {
    if (!obs || obs.length < 1) return;
    const { parameter_obs: parameterObs, timestamps } = obs;

    const transformedObs = [];

    // Convert timestamps to ISO strings
    const formattedTimestamps = timestamps.map((ts) => new Date(ts * 1000).toISOString());

    const datasetObs = Object.entries(parameterObs.values)
      .map(([paramId, values]) => ({
        parameter_id: Number(paramId),
        observations: values.map((value, index) => ({
          timestamp: formattedTimestamps[index],
          value,
        })).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
      }))
      .sort((a, b) => a.parameter_id - b.parameter_id);

    transformedObs.push({
      obs_dataset_id: Number(id),
      parameters: datasetObs,
    });

    return transformedObs;
  });

  const parameterObs = transformObsDataV2(obsDataV2);

  const {
    data: organizationsResult,
  } = useQuery(MapQueryTypes.REST_ORGANIZATIONS, getOrganizations, { refetchOnWindowFocus: false });

  const {
    data: platformMetadataResult,
    refetch: refetchPlatformMetadata,
  } = useQuery([MapQueryTypes.REST_PLATFORM_METADATA, datasetIds],
    () => getPlatformMetadata(datasetIds[0]),
    {
      refetchOnWindowFocus: false,
      enabled: !!datasetIds.length,
    });

  useEffect(() => {
    if (obsDatasetSummariesResult) {
      if (isPlatformsDataConsole) {
        setObsDataset(obsDatasetSummariesResult.find((o) => !!o.obs_dataset_id));
        setDatasetIds(obsDatasetSummariesResult.map((s) => s.obs_dataset_id));
      } else {
        setObsDataset(obsDatasetSummariesResult.find((o) => datasetIds.includes(o.obs_dataset_id)));
      }
    }
  }, [obsDatasetSummariesResult]);

  useEffect(() => {
    let date;
    let latestDatasetIds;
    if (obsLatestDataResult && obsDataset) {
      latestDatasetIds = obsLatestDataResult.map((o) => o.obs_dataset_id).filter((o) => datasetIds.includes(o));
      // setDatasetIdsLatest(latestDatasetIds);
      date = getLatestParameterDate(obsDataset, obsLatestDataResult, true);

      setHeaderInfo({
        last_updated_parameter_in_hours: moment().diff(moment(new Date(date)), 'hours'),
        platform_event: obsDataset.obs_dataset_platform_assignment?.platform?.platform_event?.event,
        organization_id: obsDataset.obs_dataset_platform_assignment?.platform?.organization_id,
      });
    }
    if (!dataStartDate && obsDataset && date) {
      if (isPlatformsDataConsole) {
        latestDatasetIds.forEach((id) => {
          const datasetObsDate = getLatestParameterDate(obsDatasetSummariesResult.find((o) => o.obs_dataset_id === id), obsLatestDataResult, true);
          if (datasetObsDate < date) {
            date = datasetObsDate;
          }
        });
      }
      const dateParam = date ? moment(date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
      setDataStartDate(dateParam);
    }
  }, [obsLatestDataResult, obsDataset]);

  useEffect(() => {
    if (!!datasetIds.length && !platformMetadataResult) {
      refetchPlatformMetadata();
    }
  }, [datasetIds, platformMetadataResult]);

  const parameters = getParameters({
    dataset: obsDataset,
    obsData: parameterObs,
    parameterConfigurations: parameterConfigurationsResult,
    registeredParameters: registeredParametersResult,
    unitPreferences,
  });

  useEffect(() => {
    if (obsDataset && organizationsResult && organizationsResult.length > 0) {
      const organization = organizationsResult.find((o) => o.organization_id === obsDataset.organization_id);
      setOrganization(organization);
    }
  }, [obsDataset, organizationsResult]);

  const getChartData = useCallback((parameter) => {
    const chartDataFiltered = [];
    const timestampArr = [];

    const parametersArr = parameterObs?.find((o) => o.obs_dataset_id === obsDataset.obs_dataset_id)?.parameters;

    const observationsArr = normalizeParameterObs(parameter, parametersArr);

    const thresholdMin = parameter.alerts?.threshold_min;
    const thresholdMax = parameter.alerts?.threshold_max;

    observationsArr.forEach((observation) => {
      // * 1000 to convert Unix timestamp in seconds to milliseconds for JavaScript/moment.js
      const obsTimestamp = Number.isNaN(Number(observation?.timestamp))
        ? new Date(observation?.timestamp).getTime()
        : new Date(observation?.timestamp * 1000).getTime();

      if (!timestampArr.includes(obsTimestamp)) {
        if (observation.value !== null) {
          const withinMin = thresholdMin == null || observation.value >= thresholdMin;
          const withinMax = thresholdMax == null || observation.value <= thresholdMax;
          if (withinMin && withinMax) {
            const observationValue = formatParameterObservation(parameterConfigurationsResult, parameter, observation, unitPreferences);
            if (observationValue !== null && observationValue !== undefined) {
              chartDataFiltered.push([obsTimestamp, observationValue]);
              timestampArr.push(obsTimestamp);
            }
          }
        }
      }
    });

    // Fallback if alert thresholds do not align with values for some reason
    if (chartDataFiltered.length < 1) {
      observationsArr.forEach((observation) => {
        // * 1000 to convert Unix timestamp in seconds to milliseconds for JavaScript/moment.js
        const obsTimestamp = Number.isNaN(Number(observation?.timestamp))
          ? new Date(observation?.timestamp).getTime()
          : new Date(observation?.timestamp * 1000).getTime();

        if (!timestampArr.includes(obsTimestamp)) {
          if (observation.value !== null) {
            const observationValue = formatParameterObservation(parameterConfigurationsResult, parameter, observation, unitPreferences);
            if (observationValue !== null && observationValue !== undefined) {
              chartDataFiltered.push([obsTimestamp, observationValue]);
              timestampArr.push(obsTimestamp);
            }
          }
        }
      });
    }

    return chartDataFiltered;
  }, [parameterConfigurationsResult, parameterObs, obsDataset]);

  const breadcrumb = [
    {
      name: obsDataset?.platform_name,
      href: `/data-console/${datasetIds[0]}`,
    },
  ];

  return (
    <div className="console-detail">
      <DetailHeader
        breadcrumb={breadcrumb}
        dataset={obsDataset}
        isMobile={isMobile}
        organization={organization}
        parameters={parameters}
        isPlatformsDataConsole={isPlatformsDataConsole}
        statusEl={(
          obsDataset && shouldRenderStatusMarker ? (
            <MarkerStatus
              properties={headerInfo}
              iconProps={{
                height: headerInfo?.organization_id === brandingConfig.filters.organizationId ? '16px' : '24px',
                width: headerInfo?.organization_id === brandingConfig.filters.organizationId ? '16px' : '24px',
              }}
            />
          ) : <span />
        )}
      />
      {collectionStatus && (
        <CollectionStatusAlert collectionStatus={collectionStatus} />
      )}
      <ParametersList
        platform={obsDataset}
        metadata={platformMetadataResult}
        parameters={parameters}
        isMobile={isMobile}
        isPlatformsConsole={isPlatformsDataConsole}
        onGetChartData={getChartData}
        collectionStatus={collectionStatus}
        obsLatestDataResult={obsLatestDataResult}
        isLoading={
          obsDataV2IsLoading
          || obsLatestIsLoading
          || obsDatasetSummariesIsLoading
          || parameterConfigurationsIsLoading
          || registeredParametersIsLoading
        }
      />
    </div>
  );
};

DataConsoleDetail.propTypes = {};

DataConsoleDetail.defaultProps = {};

export default memo(DataConsoleDetail);
